import { forwardRef } from "react";
import logoEnglputzeder from "@/assets/logo-englputzeder.png";
import { teamDe } from "@/lib/teamNames";

// ============================================================
// Englputzeder WM 2026 — Instagram Story generator canvas.
//
// Faithful port of the designer's template (englputzeder_stories):
//   • Base design is 300 × 533 px, Montserrat, warm-beige #F2EDE4
//   • We render that base at native size, then `transform: scale(3.6)`
//     it up into a 1080 × 1920 wrapper — the exact IG story export size
//     (300×3.6 = 1080, 533×3.6 = 1919.8). Keeping the base values means
//     this file mirrors the template 1:1; no per-value math.
//
// Three story types, switched on `data.variant`:
//   1. nextMatchday — a matchday (12:00 → 12:00 next day, Vienna time)
//      split into up to two dated sections (day 1: 12:00–24:00, day 2:
//      00:00–12:00), up to 6 games total + community favourite per game
//   2. bestTipper   — top 4 individual tippers (rank/name/club/points)
//   3. topVereine   — top 4 Tippgruppen (rank/name/members/points)
// ============================================================

const RED = "#B01E23";
const BLACK = "#1a1a1a";
const CREAM = "#F2EDE4";
const FONT = "'Montserrat', system-ui, sans-serif";

// Base → export scale. 300×533 base * 3.6 = 1080×1919.8 ≈ IG story.
const SCALE = 3.6;
const BASE_W = 300;
const BASE_H = 533;

export type StoryVariant = "nextMatchday" | "bestTipper" | "topVereine";

export type StoryMatch = {
  home_team: string;
  home_flag: string;
  away_team: string;
  away_flag: string;
  time: string; // "21:00"
  fav_flag: string; // community favourite team flag
  fav_pct: number; // % of community backing that side
  has_tips: boolean;
};

export type StoryTipper = { rank: number; username: string; club: string | null; points: number };
export type StoryClub = { rank: number; name: string; members: number; points: number };

// One dated section of the matchday (day 1 = 12:00–24:00, day 2 = 00:00–12:00).
export type StoryDaySection = {
  dateLong: string; // e.g. "Sonntag, 19. Juli 2026"
  matches: StoryMatch[];
};

// Kept for backwards-compat with StoryGeneratorPanel's old import name.
export type StoryRanking = StoryClub;

export type StoryData = {
  variant: StoryVariant;
  dateShort: string; // redbox, e.g. "Di, 09. Juni 2026"
  days: StoryDaySection[]; // matchday split into its real calendar days
  tippers: StoryTipper[];
  clubs: StoryClub[];
};

export const DailyStoryCanvas = forwardRef<HTMLDivElement, { data: StoryData }>(
  ({ data }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          width: BASE_W * SCALE,
          height: BASE_H * SCALE,
          overflow: "hidden",
          background: CREAM,
          position: "relative",
        }}
      >
        <div
          style={{
            width: BASE_W,
            height: BASE_H,
            transform: `scale(${SCALE})`,
            transformOrigin: "top left",
            position: "relative",
            background: CREAM,
            fontFamily: FONT,
            overflow: "hidden",
          }}
        >
          {/* Rotes Rechteck oben rechts. Top-right corner squared for
              full-bleed export; bottom-left rounded as the design accent. */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 130,
              height: 95,
              background: RED,
              borderRadius: "0 0 0 14px",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 8,
            }}
          >
            <div
              style={{
                fontWeight: 900,
                fontSize: 13,
                color: "#fff",
                textTransform: "uppercase",
                letterSpacing: 1.5,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              WM 2026
              <br />
              Tippspiel
            </div>
            <div
              style={{
                fontSize: 8,
                fontWeight: 600,
                color: "rgba(255,255,255,0.75)",
                marginTop: 5,
                textAlign: "center",
              }}
            >
              {data.dateShort}
            </div>
          </div>

          {/* Logo oben links — direkt auf Beige */}
          <div style={{ padding: "16px 16px 0", position: "relative", zIndex: 2 }}>
            <img
              src={logoEnglputzeder}
              alt="Englputzeder"
              crossOrigin="anonymous"
              style={{ height: 16, width: "auto", display: "block" }}
            />
          </div>

          {/* Body per variant */}
          {data.variant === "nextMatchday" && <NextMatchdayBody data={data} />}
          {data.variant === "bestTipper" && <RankBody data={data} kind="tipper" />}
          {data.variant === "topVereine" && <RankBody data={data} kind="club" />}
        </div>
      </div>
    );
  },
);

DailyStoryCanvas.displayName = "DailyStoryCanvas";

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: BLACK,
        color: "#fff",
        fontWeight: 800,
        fontSize: 8.5,
        letterSpacing: 2,
        textTransform: "uppercase",
        padding: "5px 12px",
        borderRadius: 4,
        display: "inline-block",
        margin: "62px 16px 8px",
      }}
    >
      {children}
    </div>
  );
}

// ---- STORY 1: Nächster Spieltag ----
function NextMatchdayBody({ data }: { data: StoryData }) {
  // Sections with no games are dropped upstream; here we only render what's left.
  const days = data.days.filter((d) => d.matches.length > 0);
  const totalGames = days.reduce((n, d) => n + d.matches.length, 0);
  // The base template is sized for ~4 games; when a matchday spans two days
  // and brings up to 6 games, switch to a tighter layout so it still fits.
  const compact = totalGames > 4;

  return (
    <>
      <Badge>Nächster Spieltag</Badge>

      {days.length === 0 && (
        <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: 30 }}>
          Kein Spieltag in Sicht.
        </div>
      )}

      {days.map((day, di) => (
        <div key={di}>
          {/* Datums-Überschrift pro Tag */}
          <div
            style={{
              color: RED,
              fontWeight: 700,
              fontSize: compact ? 9 : 10,
              padding: di === 0 ? "0 16px 5px" : "7px 16px 5px",
            }}
          >
            {day.dateLong}
          </div>
          <div style={{ margin: "0 16px", display: "flex", flexDirection: "column", gap: compact ? 4 : 6 }}>
            {day.matches.map((g, i) => (
              <MatchCard key={i} g={g} compact={compact} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function MatchCard({ g, compact }: { g: StoryMatch; compact: boolean }) {
  const flagSize = compact ? 15 : 17;
  const teamSize = compact ? 8 : 8.5;
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 7,
        padding: compact ? "6px 9px" : "8px 10px",
        border: "0.5px solid rgba(0,0,0,0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: compact ? 4 : 5,
        }}
      >
        {/* Heimteam — Flagge links */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: flagSize, lineHeight: 1, flexShrink: 0 }}>{g.home_flag}</span>
          <span style={{ fontWeight: 700, fontSize: teamSize, color: BLACK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {teamDe(g.home_team)}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, padding: "0 6px" }}>
          <span style={{ fontWeight: 900, fontSize: 11, color: RED }}>VS</span>
          <span style={{ fontSize: 7.5, fontWeight: 600, color: "#888" }}>{g.time}</span>
        </div>
        {/* Auswärtsteam — Flagge rechts */}
        <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 5, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: flagSize, lineHeight: 1, flexShrink: 0 }}>{g.away_flag}</span>
          <span style={{ fontWeight: 700, fontSize: teamSize, color: BLACK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "right" }}>
            {teamDe(g.away_team)}
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          paddingTop: compact ? 3 : 4,
          marginTop: 2,
        }}
      >
        <span style={{ fontSize: 7, fontWeight: 700, color: "#aaa", letterSpacing: 0.5, textTransform: "uppercase" }}>
          Community-Tipp
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {g.has_tips ? (
            <>
              <div
                style={{
                  width: compact ? 18 : 20,
                  height: compact ? 18 : 20,
                  borderRadius: "50%",
                  border: `2px solid ${RED}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: compact ? 11 : 12,
                  flexShrink: 0,
                }}
              >
                {g.fav_flag}
              </div>
              <span style={{ fontSize: 8, fontWeight: 900, color: RED }}>{g.fav_pct}%</span>
            </>
          ) : (
            <span style={{ fontSize: 8, fontWeight: 700, color: "#aaa" }}>Noch keine Tipps</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- STORY 2 & 3: Ranking-Liste (Tipper bzw. Vereine) ----
function RankBody({ data, kind }: { data: StoryData; kind: "tipper" | "club" }) {
  const rows: { rank: number; name: string; sub: string; points: number }[] =
    kind === "tipper"
      ? data.tippers.slice(0, 4).map((t) => ({
          rank: t.rank,
          name: `@${t.username}`,
          sub: t.club ?? "Einzelspieler",
          points: t.points,
        }))
      : data.clubs.slice(0, 4).map((c) => ({
          rank: c.rank,
          name: c.name,
          sub: `${c.members} Mitglied${c.members === 1 ? "" : "er"}`,
          points: c.points,
        }));
  const ptsLabel = kind === "tipper" ? "Pkt" : "Punkte";

  return (
    <>
      <Badge>{kind === "tipper" ? "Bester Tipper" : "Gesamtranking"}</Badge>

      {kind === "club" && (
        <div style={{ padding: "4px 16px 8px" }}>
          <div
            style={{
              fontWeight: 900,
              fontSize: 28,
              color: BLACK,
              lineHeight: 1.05,
              textTransform: "uppercase",
            }}
          >
            Top Vereine
            <span style={{ color: RED, display: "block" }}>gesamt.</span>
          </div>
        </div>
      )}

      <div style={{ margin: "0 16px", display: "flex", flexDirection: "column", gap: 5 }}>
        {rows.map((r) => {
          const first = r.rank === 1;
          const sm = r.rank >= 4; // Rang 1-3 = big, Rang 4 = sm
          return (
            <div
              key={r.rank}
              style={{
                background: first ? RED : "#fff",
                borderRadius: 7,
                padding: "7px 10px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: first ? `0.5px solid ${RED}` : "0.5px solid rgba(0,0,0,0.07)",
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  color: first ? "#fff" : RED,
                  flexShrink: 0,
                  textAlign: "center",
                  width: 16,
                  fontSize: sm ? 12 : 17,
                }}
              >
                {r.rank}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 800,
                    color: first ? "#fff" : BLACK,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: sm ? 9 : 11,
                  }}
                >
                  {r.name}
                </div>
                <div
                  style={{
                    color: first ? "rgba(255,255,255,0.65)" : "#888",
                    marginTop: 1,
                    fontSize: sm ? 7 : 8,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {r.sub}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <span
                  style={{
                    fontWeight: 900,
                    color: first ? "#fff" : BLACK,
                    lineHeight: 1,
                    display: "block",
                    fontSize: sm ? 14 : 19,
                  }}
                >
                  {r.points}
                </span>
                <span
                  style={{
                    fontSize: 7,
                    color: first ? "rgba(255,255,255,0.65)" : "#888",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {ptsLabel}
                </span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: 30 }}>
            Noch keine Punkte vergeben.
          </div>
        )}
      </div>
    </>
  );
}
